import React, { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getQuoteContext } from "@/lib/api";
import { Loader2, ExternalLink, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Provides a single app-wide quote expansion dialog.
 * Any <Quote> can call ctx.open(quote) to show full surrounding source context.
 */
const QuoteCtx = createContext(null);

export function QuoteContextProvider({ measureDbId, children }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [currentQuote, setCurrentQuote] = useState("");

  const show = useCallback(
    async (quote) => {
      if (!quote || !measureDbId) return;
      setCurrentQuote(quote);
      setOpen(true);
      setLoading(true);
      try {
        const d = await getQuoteContext(measureDbId, quote);
        setData(d);
      } catch (e) {
        setData({ found: false, note: "Lookup failed." });
      } finally {
        setLoading(false);
      }
    },
    [measureDbId]
  );

  const copy = () => {
    if (data?.context) {
      navigator.clipboard.writeText(data.context);
      toast.success("Copied");
    }
  };

  return (
    <QuoteCtx.Provider value={{ show }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Full source context
            </DialogTitle>
            <DialogDescription className="text-xs font-mono uppercase tracking-wider">
              {data?.found
                ? (
                    <>
                      {data.source_title}
                      {data.page ? ` · p. ${data.page}` : ""}
                      {data.pdf && !data.page ? " · PDF (page not detected)" : ""}
                    </>
                  )
                : "Locating quote in source documents…"}
            </DialogDescription>
            {data?.found && (
              <div className="mt-2 text-[11px] text-[hsl(var(--ink-muted))] leading-relaxed normal-case font-sans">
                <span className="font-mono uppercase tracking-wider">Verify this yourself:</span>{" "}
                Each quote includes the official source, bill version, page number when
                available, and section/code references when available. For HTML sources
                without page numbers, use the section/code reference and official link.
              </div>
            )}
          </DialogHeader>

          {loading && (
            <div className="py-8 text-center text-sm text-[hsl(var(--ink-muted))]">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
              Looking up context…
            </div>
          )}

          {!loading && data?.found && (
            <div className="space-y-3">
              <div className="text-[13px] leading-relaxed font-mono bg-[hsl(var(--paper-2))] border border-[hsl(var(--rule))] p-4 rounded-sm max-h-[50vh] overflow-y-auto" data-testid="quote-context-body">
                {highlight(data.context, currentQuote)}
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                {data.source_url && !data.source_url.startsWith("manual://") && !data.source_url.startsWith("scan://") && (
                  <a
                    href={data.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="pill-btn pill-btn-ghost text-xs"
                    data-testid="quote-source-link"
                  >
                    <ExternalLink className="w-3 h-3" /> Open source
                  </a>
                )}
                <button
                  type="button"
                  onClick={copy}
                  className="pill-btn pill-btn-ghost text-xs"
                  data-testid="quote-copy"
                >
                  <Copy className="w-3 h-3" /> Copy paragraph
                </button>
                {data.source_type && (
                  <span className="text-[11px] font-mono text-[hsl(var(--ink-muted))] uppercase tracking-wider ml-auto">
                    {data.source_type}
                  </span>
                )}
              </div>
            </div>
          )}

          {!loading && data && data.found === false && (
            <div className="py-4 flex items-start gap-3 text-sm" data-testid="quote-not-found">
              <AlertTriangle className="w-4 h-4 text-[hsl(var(--accent-amber))] shrink-0 mt-0.5" />
              <div>
                <div className="text-[hsl(var(--ink-2))] mb-2">
                  {data.note || "Quote not found in source."}
                </div>
                <div className="quote-block">&ldquo;{currentQuote}&rdquo;</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </QuoteCtx.Provider>
  );
}

function highlight(context, quote) {
  if (!quote) return context;
  // case-insensitive, whitespace-tolerant highlight of the quote within the context
  const tokens = quote
    .trim()
    .replace(/["“”']/g, "")
    .split(/\s+/)
    .slice(0, 12)
    .join("\\s+");
  if (!tokens) return context;
  try {
    const re = new RegExp(`(${tokens})`, "i");
    const parts = context.split(re);
    return parts.map((p, i) =>
      re.test(p) ? (
        <mark key={i} className="bg-[hsl(var(--accent-amber)/0.35)] text-[hsl(var(--ink))] rounded-sm px-0.5">
          {p}
        </mark>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  } catch {
    return context;
  }
}

export function useQuoteContext() {
  return useContext(QuoteCtx);
}
